import { useState, useMemo } from 'react';
import { subDays, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Truck, FileText, RefreshCw, Package } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useHasAnyRole } from '@/hooks/useSession';
import { useShipments, useInvoices, usePackageStatuses } from '../hooks/usePlaneteCourrier';
import { pcKeys } from '../hooks/queryKeys';
import { ShipmentsTable } from '../components/ShipmentsTable';
import { ShipmentDetailPanel } from '../components/ShipmentDetailPanel';
import { InvoicesTable } from '../components/InvoicesTable';
import { AgingSummary } from '../components/AgingSummary';
import { CreateShippingLabelModal } from '../components/CreateShippingLabelModal';
import { PackagePresetsManager } from '../components/PackagePresetsManager';
import type { ShipmentSearchFilters, InvoiceSearchFilters } from '../types';

const ACCOUNT_NUMBER = '20180337';
const DEFAULT_DAYS_BACK = 30;
const OPS_ROLES = ['full_admin', 'director', 'operations_manager'];

export function PlaneteCourrierPage() {
  const { t } = useTranslation('planete-courrier');
  const queryClient = useQueryClient();
  const canManageShipping = useHasAnyRole(OPS_ROLES);
  const [tab, setTab] = useState('shipments');
  const [selectedShipment, setSelectedShipment] = useState<string | null>(null);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);

  // Shipment filters
  const [search, setSearch] = useState('');
  const [daysBack, setDaysBack] = useState(DEFAULT_DAYS_BACK);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [sortBy, setSortBy] = useState('shipmentDate');
  const [sortOrder, setSortOrder] = useState<-1 | 1>(-1);

  const { data: packageStatuses } = usePackageStatuses();

  const shipmentFilters = useMemo<ShipmentSearchFilters>(() => ({
    recordOffset: page * pageSize,
    pageSize,
    sortBy,
    sortOrder,
    shipmentDateStartFilter: startOfDay(subDays(new Date(), daysBack)).toISOString(),
    shipmentDateEndFilter: new Date().toISOString(),
    ...(search ? { shipmentNumberFilter: search } : {}),
    ...(statusFilter !== 'all' ? { statusCodeFilter: [statusFilter] } : {}),
  }), [page, pageSize, sortBy, sortOrder, daysBack, search, statusFilter]);

  const { data: shipmentsData, isLoading: shipmentsLoading } = useShipments(shipmentFilters);

  // Invoice filters
  const invoiceFilters = useMemo<InvoiceSearchFilters>(() => ({
    recordOffset: 0,
    pageSize: 100,
    sortOrder: -1,
    sortBy: 'invoiceDate',
    accountNumbersFilter: [ACCOUNT_NUMBER],
  }), []);

  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices(invoiceFilters);

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 1 ? -1 : 1));
    } else {
      setSortBy(key);
      setSortOrder(-1);
    }
    setPage(0);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: pcKeys.all });
  };

  // The shipment search returns `hasMoreRecords` (cursor-style), not a total count,
  // so pagination is driven by "is there a next page" rather than total-page math.
  const shipmentRecords = shipmentsData?.records ?? [];
  const shipmentHasMore = shipmentsData?.hasMoreRecords ?? false;
  const shipmentRangeStart = page * pageSize + 1;
  const shipmentRangeEnd = page * pageSize + shipmentRecords.length;

  // Compute invoice totals
  const invoiceTotal = invoicesData?.records.reduce((sum, inv) => sum + inv.amount, 0) ?? 0;

  return (
    <div className="flex h-full">
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Planète Courrier</h1>
              <p className="text-sm text-muted-foreground">
                Account {ACCOUNT_NUMBER} · Atek (Hakim Rouab)
              </p>
            </div>
            <div className="flex items-center gap-2">
              {canManageShipping && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setLabelModalOpen(true)}>
                    <Truck className="h-4 w-4 mr-1" /> {t('actions.newLabel')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPresetsOpen(true)}>
                    <Package className="h-4 w-4 mr-1" /> {t('actions.packageSizes')}
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
            </div>
          </div>

          {/* Aging */}
          <AgingSummary accountNumber={ACCOUNT_NUMBER} />

          {/* Tabs */}
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="shipments" className="gap-1">
                <Truck className="h-4 w-4" />
                Shipments
                {shipmentsData && shipmentRecords.length > 0 && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({shipmentRangeEnd}{shipmentHasMore ? '+' : ''})
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="invoices" className="gap-1">
                <FileText className="h-4 w-4" />
                Invoices
                {invoicesData && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({invoicesData.totalRecords})
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Shipments Tab */}
            <TabsContent value="shipments" className="space-y-4">
              <div className="flex items-center gap-3">
                <Input
                  placeholder="Search by shipment number..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  className="max-w-xs"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                  className="border rounded px-3 py-2 text-sm"
                >
                  <option value="all">All Statuses</option>
                  {packageStatuses?.map((s) => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
                <select
                  value={daysBack}
                  onChange={(e) => { setDaysBack(Number(e.target.value)); setPage(0); }}
                  className="border rounded px-3 py-2 text-sm"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={60}>Last 60 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={180}>Last 6 months</option>
                  <option value={365}>Last year</option>
                </select>
              </div>

              <ShipmentsTable
                shipments={shipmentRecords}
                isLoading={shipmentsLoading}
                onSelect={setSelectedShipment}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              {/* Pagination — cursor-style: the shipment search reports only whether a
                  next page exists (hasMoreRecords), not a total count. */}
              {(page > 0 || shipmentHasMore) && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {shipmentRangeStart}–{shipmentRangeEnd}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!shipmentHasMore}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Invoices Tab */}
            <TabsContent value="invoices" className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Total invoiced: <span className="font-semibold text-foreground">${invoiceTotal.toFixed(2)}</span>
                </p>
              </div>
              <InvoicesTable invoices={invoicesData?.records ?? []} isLoading={invoicesLoading} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedShipment && (
        <ShipmentDetailPanel
          shipmentNumber={selectedShipment}
          onClose={() => setSelectedShipment(null)}
        />
      )}

      {canManageShipping && (
        <>
          <CreateShippingLabelModal open={labelModalOpen} onOpenChange={setLabelModalOpen} />
          <PackagePresetsManager open={presetsOpen} onOpenChange={setPresetsOpen} />
        </>
      )}
    </div>
  );
}
